import { DeviceType, SortOrder } from 'api/admin/washboard/washboard';
import httpClient from '../../httpClient';

export { DeviceType, SortOrder };

export const FIRMWARE_RELEASE_SORTABLE_FIELDS = [
  'version',
  'deviceType',
  'createdAt',
  'numberOfDevices',
] as const;

export type FirmwareReleaseSortableField = (typeof FIRMWARE_RELEASE_SORTABLE_FIELDS)[number];

export type FirmwareReleasePackage = {
  name: string;
  version: string;
};

export type FirmwareRelease = {
  id: number;
  version: string;
  packages: FirmwareReleasePackage[];
  deviceType: DeviceType;
  description: string | null;
  createdAt: string;
  numberOfDevices: number;
  isArchived: boolean;
};

export type GetFirmwareReleasesParams = {
  includeArchived?: boolean;
  page: number;
  limit: number;
  sortBy?: FirmwareReleaseSortableField;
  sortOrder?: SortOrder;
};

export type GetFirmwareReleasesResponse = {
  releases: FirmwareRelease[];
  total: number;
  totalPages: number;
  currentPage: number;
};

export type ReleaseVersion = {
  id: number;
  version: string;
};

export type GetReleaseVersionsParams = {
  deviceType: DeviceType;
  page: number;
  limit: number;
};

export type GetReleaseVersionsResponse = {
  releases: ReleaseVersion[];
  total: number;
  totalPages: number;
  currentPage: number;
};

export type CancelDeviceFirmwareUpdateResponse = {
  success: boolean;
};

export type ArchiveFirmwareReleasesParams = {
  releaseIds: number[];
  archived: boolean;
};

export const GLOBAL_FIRMWARE_DEVICE_TYPES = [
  DeviceType.DIME,
  DeviceType.PENNY,
  DeviceType.PULSE,
] as const;

export type GlobalFirmwareDeviceType = (typeof GLOBAL_FIRMWARE_DEVICE_TYPES)[number];

export type FirmwareAdminSetting = {
  releaseId: number;
  version: string;
};

export type GetFirmwareAdminSettingsResponse = Record<
  GlobalFirmwareDeviceType,
  FirmwareAdminSetting | null
>;

export type SetFirmwareAdminSettingsParams = {
  defaultReleases: Array<{ deviceType: GlobalFirmwareDeviceType; releaseId: number }>;
};

export const getFirmwareReleases = (params: GetFirmwareReleasesParams) => {
  return httpClient({
    method: 'GET',
    url: 'super-admin/firmware-releases',
    params,
  });
};

export const getReleaseVersions = (params: GetReleaseVersionsParams) => {
  return httpClient({
    method: 'GET',
    url: 'super-admin/firmware-releases/versions',
    params,
  });
};

export const cancelDeviceFirmwareUpdate = (deviceId: number) => {
  return httpClient({
    method: 'POST',
    url: `super-admin/firmware-releases/devices/${deviceId}/cancel`,
  });
};

export const archiveFirmwareReleases = ({
  releaseIds,
  archived,
}: ArchiveFirmwareReleasesParams) => {
  return httpClient({
    method: 'POST',
    url: 'super-admin/firmware-releases/archive',
    data: {
      releaseIds,
      archived,
    },
  });
};

export const getFirmwareAdminSettings = () => {
  return httpClient({
    method: 'GET',
    url: 'super-admin/firmware-admin-settings',
  });
};

export const setFirmwareAdminSettings = ({ defaultReleases }: SetFirmwareAdminSettingsParams) => {
  return httpClient({
    method: 'PUT',
    url: 'super-admin/firmware-admin-settings',
    data: {
      defaultReleases,
    },
  });
};
